/* ============================================================
   ATLAS — Cart Upsell
   Fetches Shopify Recommendations API (intent=complementary)
   for the first cart product and renders 10 mini cards inside
   any [data-atl-cart-upsell] container. Falls back to /products.json
   featured products when cart is empty or no recommendations.
   ============================================================ */
(function () {
  'use strict';

  const _ATL_CUR = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
  const _ATL_LOCALE = (window.Shopify && Shopify.locale) || document.documentElement.lang || 'en';
  let _atlNf = null;
  const FORMATTER = (cents) => {
    try {
      if (!_atlNf) _atlNf = new Intl.NumberFormat(_ATL_LOCALE, { style: 'currency', currency: _ATL_CUR });
      return _atlNf.format((cents || 0) / 100);
    } catch (e) {
      if (window.Shopify && window.Shopify.formatMoney) return window.Shopify.formatMoney(cents, window.Shopify.money_format || '${{amount}}');
      return '$' + ((cents || 0) / 100).toFixed(2);
    }
  };

  const LIMIT = 10;
  const fetched = new Map(); // cache by product_id

  function getCartProductId(container) {
    // 1. data-product-id on the container (set by Liquid)
    const fromAttr = container.getAttribute('data-product-id');
    if (fromAttr && fromAttr !== '0' && fromAttr !== '') return fromAttr;

    // 2. fallback: scan cart item rows
    const row = document.querySelector('[data-product-id], .cart-items__table-row[data-line-item-product-id]');
    if (row) {
      return row.getAttribute('data-product-id') || row.getAttribute('data-line-item-product-id');
    }
    return null;
  }

  async function fetchRecommendations(productId) {
    if (!productId) return null;
    if (fetched.has(productId)) return fetched.get(productId);
    try {
      const url = `/recommendations/products.json?product_id=${productId}&limit=${LIMIT}&intent=complementary`;
      const res = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!res.ok) throw new Error('reco fetch failed: ' + res.status);
      const data = await res.json();
      fetched.set(productId, data.products || []);
      return data.products || [];
    } catch (e) {
      console.warn('[atlas-cart-upsell]', e);
      return null;
    }
  }

  async function fetchFallback() {
    try {
      const res = await fetch('/products.json?limit=' + LIMIT, { headers: { Accept: 'application/json' } });
      if (!res.ok) return [];
      const data = await res.json();
      // /products.json shape differs (handle, variants[].price as string) — normalize
      return (data.products || []).map((p) => ({
        id: p.id,
        title: (window.atlNom ? atlNom(p) : p.title),
        url: (window.Shopify.routes.root + 'products/') + p.handle,
        featured_image: p.images && p.images[0] ? p.images[0].src : (p.image ? p.image.src : ''),
        price: Math.round(parseFloat(p.variants[0].price) * 100),
        compare_at_price: p.variants[0].compare_at_price ? Math.round(parseFloat(p.variants[0].compare_at_price) * 100) : null,
        variants: p.variants.map((v) => ({ id: v.id, available: v.available })),
      }));
    } catch (e) {
      console.warn('[atlas-cart-upsell] fallback failed', e);
      return [];
    }
  }

  function cardHtml(product) {
    const img = product.featured_image || '';
    const imgSrc = typeof img === 'string'
      ? img.replace(/(\.[a-z]+)(\?|$)/i, '_300x$1$2')
      : '';
    const variantId = (product.variants && product.variants[0] && product.variants[0].id) || '';
    const onSale = product.compare_at_price && product.compare_at_price > product.price;
    const priceHtml = onSale
      ? `<span class="on-sale">${FORMATTER(product.price)}</span><s>${FORMATTER(product.compare_at_price)}</s>`
      : `<span>${FORMATTER(product.price)}</span>`;

    return `
      <div class="atl-cart-upsell__card">
        <a href="${product.url}" class="atl-cart-upsell__media">
          ${imgSrc ? `<img src="${imgSrc}" alt="${product.title.replace(/"/g,'&quot;')}" loading="lazy">` : ''}
        </a>
        <a href="${product.url}" style="text-decoration:none;color:inherit;">
          <p class="atl-cart-upsell__name">${product.title}</p>
        </a>
        <div class="atl-cart-upsell__price">${priceHtml}</div>
        <button class="atl-cart-upsell__add" type="button" data-variant-id="${variantId}">+ Add</button>
      </div>
    `;
  }

  async function addToCart(variantId, btn) {
    if (!variantId) return;
    btn.disabled = true;
    const original = btn.textContent;
    btn.textContent = 'Adding…';
    try {
      // Collect all cart-items-component section IDs so Horizon can morph them in place
      const sectionIds = Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
        .map((el) => el.getAttribute('data-section-id'))
        .filter(Boolean);

      const body = { items: [{ id: Number(variantId), quantity: 1 }] };
      if (sectionIds.length) body.sections = sectionIds.join(',');

      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const errBody = await res.text().catch(() => '');
        throw new Error('cart/add failed: ' + res.status + ' ' + errBody);
      }
      const data = await res.json();

      btn.textContent = '✓ Added';
      btn.classList.add('is-added');

      // Dispatch Horizon's cart:update event with sections so cart-items-component morphs in place
      const detail = {
        resource: data,
        sourceId: 'atlas-upsell',
        data: {
          source: 'atlas-upsell',
          sections: data.sections || {},
          variantId: String(variantId),
          itemCount: data.item_count,
        },
      };
      const updateEvent = new CustomEvent('cart:update', { bubbles: true, detail });
      document.dispatchEvent(updateEvent);

      // Also fetch fresh /cart.js to refresh cart bubble counters (Horizon does this from cart events)
      try {
        await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      } catch (_) {}

      setTimeout(() => {
        if (!btn.isConnected) return;
        btn.disabled = false;
        btn.textContent = original;
        btn.classList.remove('is-added');
      }, 1800);
    } catch (e) {
      console.warn('[atlas-cart-upsell] add failed', e);
      btn.textContent = 'Try again';
      setTimeout(() => {
        if (!btn.isConnected) return;
        btn.disabled = false;
        btn.textContent = original;
      }, 1500);
    }
  }

  async function render(container) {
    if (!container || container.dataset.atlRendered === '1') return;
    container.dataset.atlRendered = '1';

    const productId = getCartProductId(container);
    let products = await fetchRecommendations(productId);
    if (!products || products.length === 0) {
      products = await fetchFallback();
    }
    if (!products || products.length === 0) {
      container.hidden = true;
      return;
    }

    const title = container.getAttribute('data-title') || 'Complete the ritual';
    const arrowPrev = `<button type="button" class="atl-cart-upsell__arrow atl-cart-upsell__arrow--prev" aria-label="Previous"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg></button>`;
    const arrowNext = `<button type="button" class="atl-cart-upsell__arrow atl-cart-upsell__arrow--next" aria-label="Next"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg></button>`;
    container.innerHTML = `
      <div role="heading" aria-level="3" class="atl-cart-upsell__title">${title}</div>
      <div class="atl-cart-upsell__viewport">
        ${arrowPrev}
        <div class="atl-cart-upsell__scroller">
          ${products.map(cardHtml).join('')}
        </div>
        ${arrowNext}
      </div>
    `;

    container.querySelectorAll('.atl-cart-upsell__add').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        addToCart(btn.getAttribute('data-variant-id'), btn);
      });
    });

    bindArrows(container);
  }

  function bindArrows(container) {
    const scroller = container.querySelector('.atl-cart-upsell__scroller');
    const prev = container.querySelector('.atl-cart-upsell__arrow--prev');
    const next = container.querySelector('.atl-cart-upsell__arrow--next');
    if (!scroller || !prev || !next) return;

    const step = () => scroller.clientWidth;

    const updateState = () => {
      const max = scroller.scrollWidth - scroller.clientWidth - 1;
      prev.disabled = scroller.scrollLeft <= 1;
      next.disabled = scroller.scrollLeft >= max;
    };

    prev.addEventListener('click', () => { scroller.scrollBy({ left: -step(), behavior: 'smooth' }); });
    next.addEventListener('click', () => { scroller.scrollBy({ left: step(), behavior: 'smooth' }); });
    scroller.addEventListener('scroll', updateState, { passive: true });
    window.addEventListener('resize', updateState);
    requestAnimationFrame(updateState);
  }

  function initAll() {
    document.querySelectorAll('[data-atl-cart-upsell]').forEach((el) => {
      // Reset rendered flag if container was re-injected/morphed by a section refresh
      if (!el.children.length) el.dataset.atlRendered = '';
      render(el);
    });
  }

  // Initial run
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Observe DOM mutations for cart drawer opening / brand-new container nodes
  const observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      for (const node of m.addedNodes) {
        if (node.nodeType !== 1) continue;
        if (node.matches && node.matches('[data-atl-cart-upsell]')) {
          render(node);
        } else if (node.querySelectorAll) {
          node.querySelectorAll('[data-atl-cart-upsell]').forEach(render);
        }
      }
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });

  // CRITICAL: After Horizon's cart-items-component morphs the section in place,
  // the upsell <div> is reset to its empty Liquid template (the JS-rendered cards
  // are wiped). We listen to the cart:update event ourselves and re-render after
  // the morph settles. Two RAFs gives the morph one frame to land.
  const rerenderAfterMorph = () => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        document.querySelectorAll('[data-atl-cart-upsell]').forEach((el) => {
          el.dataset.atlRendered = '';
          render(el);
        });
      });
    });
  };
  document.addEventListener('cart:update', rerenderAfterMorph);
  document.addEventListener('cart:refresh', rerenderAfterMorph);
  document.addEventListener('dialog:open', initAll, true);
})();
